import { ReactNode, useEffect, useState } from "react";
import PrintPage from "@components/print/PrintElements/PrintPage";
import { useAppSelector } from "@/store/hooks";
import moment from "moment";
import cx from "classnames";
import styles from "./style.module.scss";
import LoadingScreen from "@components/content/LoadingScreen";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { formatCant } from "@utils/Cant";
import { CategoryApi } from "@/api/CategoryApi";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { calculateProductPrice } from "@utils/EDocument";
import { useParams } from "react-router-dom";
import { PriceApi, PriceDataType } from "@/api/PricesApi";

type ListPriceReportPrintProps = {
  excelExport?: boolean
}

type InventoryCategory = {
  title: string,
  products: ProductDataType[]
}

const ListPriceReportPrint = (props: ListPriceReportPrintProps) => {
  const params = useParams()
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [priceData, setPriceData] = useState<number | PriceDataType>();
  const { priceId = "", onlyOnStock = false } = params

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadReportData()
  }, []);

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      const priceProp = Number(priceId)
      if (isNaN(priceProp)) {
        const priceData = await PriceApi.get(priceId)
        setPriceData(priceData)
      } else {
        setPriceData(priceProp)
      }

      const categories: InventoryCategory[] = []
      const categoryList = await CategoryApi.optionsList({ activo: { eq: true } });
      for (const categoryId in categoryList) {
        let nt = undefined;
        let products: ProductDataType[] = [];
        do {
          const { items, nextToken } = await ProductApi.fetchAllByCategory(categoryId, {}, `${nt || ""}`, 1000,
            ["codigo", "descripcion_fact", "temporal", "activo", "existencias", "costo", "precio1", "precio2", "precio3", "precio4", "porcentaje_impuesto"]
          )
          let filteredProducts = items.filter(p => !p.temporal && p.activo);
          if (onlyOnStock != "false") {
            filteredProducts = filteredProducts.filter(p => p.existencias && p.existencias > 0);
          }
          products = [
            ...products,
            ...filteredProducts
          ]
          nt = nextToken
        } while (nt)

        products.sort((a, b) => {
          const nameA = a.descripcion_fact || "";
          const nameB = b.descripcion_fact || "";

          return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        })

        categories.push({
          title: categoryList[categoryId],
          products
        })
      }

      setCategories(categories);
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  const renderCategories = (): ReactNode[] => {
    return categories.map(c => {
      return (<div>
        <div className={styles.categoryTitle}>{c.title}</div>
        <table className={styles.productTable}>
          <tbody>
            {c.products.map(p => {
              return (
                <tr className={styles.productRows}>
                  <td className={styles.codigo}>{p.codigo}</td>
                  <td className={styles.description}>{p.descripcion_fact}</td>
                  <td className={styles.existencias}>{formatCant(p.existencias)}</td>
                  <td className={styles.costo}>₡ {calculateProductPrice(p, priceData)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>)
    })
  }

  return (
    <div>
      <PrintPage
        autoPrint={false}
        onReadyDebounce={2000}
      >
        <table className={styles.pageContent}>
          <thead>
            <tr>
              <th>
                <div className={styles.printHeader}>
                  <table className={cx([styles.productTable, "mt-1"])}>
                    <tbody>
                      <tr className={styles.productRows}>
                        <td className={styles.codigo}><b>Codigo</b></td>
                        <td className={styles.description}><b>Descripcion</b></td>
                        <td className={styles.existencias}><b>Existencias</b></td>
                        <td className={styles.costo}><b>Precio</b></td>
                      </tr>
                      <tr>
                        <td colSpan={4}>
                          <hr className={styles.divider} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && renderCategories()}
          </tbody>
        </table>
      </PrintPage>

      <LoadingScreen isLoading={isLoading} />

    </div>
  );
}

export default ListPriceReportPrint;
